// src/services/supabase/bidIntents.js
// Servicios para gestión de bid_intents (ofertas pendientes de verificación por email)

import { supabase } from '../../supabaseClient';

/**
 * Genera un token único seguro para el intent
 * Compatible con navegador (no usa crypto de Node.js)
 * @returns {string} Token único de 32 caracteres
 */
const generateToken = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36) + Math.random().toString(36).substring(2);
};

/**
 * Crea un bid intent (oferta pendiente de verificación)
 * @param {object} data - Datos del intent
 * @param {string} data.propertyId - ID de la propiedad
 * @param {string} data.buyerEmail - Email del comprador
 * @param {string} data.buyerName - Nombre del comprador
 * @param {number} data.amount - Monto de la oferta
 * @param {array} data.selectedConditions - Array de IDs de condiciones
 * @param {string} data.conditionsText - Texto libre de condiciones
 * @returns {Promise<object>} Intent creado con token
 */
export const createBidIntent = async ({
  propertyId,
  buyerEmail,
  buyerName,
  amount,
  selectedConditions = [],
  conditionsText = '',
  sentInStatus = 'OPEN'
}) => {
  try {
    const token = generateToken();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 10 * 60 * 1000); // 10 minutos
    console.log('🕐 Creando intent:', { now: now.toISOString(), expires: expiresAt.toISOString() });

    const { data, error } = await supabase
      .from('bid_intents')
      .insert({
        token,
        property_id: propertyId,
        buyer_email: buyerEmail,
        buyer_name: buyerName,
        amount,
        selected_conditions: Array.isArray(selectedConditions) ? selectedConditions : [],
        conditions_text: conditionsText,
        expires_at: expiresAt.toISOString().replace('Z', '+00:00'),
        used: false,
        sent_in_status: sentInStatus
      })
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error creando bid intent:', error);
    throw error;
  }
};

/**
 * Obtiene un bid intent por token
 * Valida que no esté expirado ni usado
 * @param {string} token - Token del intent
 * @returns {Promise<object|null>} Intent si es válido, null si no existe/expirado/usado
 */
export const getBidIntent = async (token) => {
  try {
    const { data, error } = await supabase
      .rpc('is_bid_intent_valid', { intent_token: token });

    if (error) {
      console.error('Error obteniendo bid intent:', error);
      throw error;
    }

    // La función SQL retorna un array, tomamos el primer elemento
    if (!data || data.length === 0) {
      return null;
    }

    return data[0];
  } catch (error) {
    console.error('Error obteniendo bid intent:', error);
    throw error;
  }
};

/**
 * Marca un intent como usado después de crear la oferta
 * @param {string} token - Token del intent
 * @returns {Promise<object>} Intent actualizado
 */
export const markIntentAsUsed = async (token) => {
  try {
    const { data, error } = await supabase
      .from('bid_intents')
      .update({
        used: true,
        used_at: new Date().toISOString()
      })
      .eq('token', token)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error marcando intent como usado:', error);
    throw error;
  }
};

/**
 * Limpia intents expirados (opcional, puede ejecutarse como cron job)
 * Elimina intents con más de 24 horas
 * @returns {Promise<number>} Número de intents eliminados
 */
export const cleanExpiredIntents = async () => {
  try {
    const yesterday = new Date();
    yesterday.setHours(yesterday.getHours() - 24);

    const { data, error } = await supabase
      .from('bid_intents')
      .delete()
      .lt('expires_at', yesterday.toISOString())
      .select();

    if (error) throw error;

    return data?.length || 0;
  } catch (error) {
    console.error('Error limpiando intents expirados:', error);
    throw error;
  }
};